import { useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import { backtestApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import ResultsPanel from '../ResultsPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import useRunResult from '../../../utils/useRunResult.js';
import { engineConfig } from '../../../utils/engineConfig.js';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

const SIGNAL_PRESETS = [
  { name: 'sma_cross', label: 'SMA Crossover', params: [{ name: 'fast', default: 10 }, { name: 'slow', default: 30 }] },
  {
    name: 'rsi_oversold',
    label: 'RSI Oversold / Overbought',
    params: [{ name: 'period', default: 14 }, { name: 'lower', default: 30 }, { name: 'upper', default: 70 }],
  },
  { name: 'breakout', label: 'N-day Breakout', params: [{ name: 'lookback', default: 20 }] },
  {
    name: 'macd_cross',
    label: 'MACD Crossover',
    params: [{ name: 'fast', default: 12 }, { name: 'slow', default: 26 }, { name: 'signal', default: 9 }],
  },
];

/**
 * Signals mode: pick one or more premade signal generators with param
 * overrides, building `signals[]` for `{mode:'signals', signals, config,
 * symbols, period}` (OBT_INTEGRATION_SPEC.md §2, BacktestRequest.mode ===
 * 'signals').
 */
export default function SignalsMode({ config, onConfigChange, wallet, onRan, lastRun, onSaveRun }) {
  const [selected, setSelected] = useState({ sma_cross: { fast: 10, slow: 30 } });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useRunResult(lastRun?.result);
  const [error, setError] = useState(null);

  function toggle(preset) {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[preset.name]) {
        delete next[preset.name];
      } else {
        const params = {};
        preset.params.forEach((p) => {
          params[p.name] = p.default;
        });
        next[preset.name] = params;
      }
      return next;
    });
  }

  function setParam(presetName, paramName, value) {
    setSelected((prev) => ({ ...prev, [presetName]: { ...prev[presetName], [paramName]: value } }));
  }

  const signals = Object.entries(selected).map(([name, params]) => ({ name, params }));

  async function run() {
    setError(null);
    if (!(Number(config.capital) > 0) || config.symbols.length === 0) return;
    setRunning(true);
    try {
      const payload = {
        mode: 'signals',
        signals,
        symbols: config.symbols,
        period: config.period,
        config: engineConfig(config),
      };
      const data = await backtestApi.run(payload);
      setResult(data);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="aq-grid aq-grid--2">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ConfigPanel config={config} onChange={onConfigChange} wallet={wallet} />

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Signal generators
          </h3>
          {SIGNAL_PRESETS.map((preset) => (
            <div key={preset.name} style={{ marginBottom: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                <input type="checkbox" checked={Boolean(selected[preset.name])} onChange={() => toggle(preset)} />
                {preset.label}
              </label>
              {selected[preset.name] && (
                <div className="aq-rule-row" style={{ marginLeft: 24, marginTop: 6 }}>
                  {preset.params.map((p) => (
                    <input
                      key={p.name}
                      type="number"
                      title={p.name}
                      style={{ width: 80 }}
                      value={selected[preset.name][p.name]}
                      onChange={(e) => setParam(preset.name, p.name, Number(e.target.value))}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}

          <StrategyPreview title="signals (request body)" value={signals} />

          <div style={{ marginTop: 14 }}>
            <Button disabled={running || signals.length === 0 || config.symbols.length === 0 || !(Number(config.capital) > 0) || !isBacktestPeriodValid(config.period)} onClick={run}>
              {running ? 'Running…' : 'Run signals backtest'}
            </Button>
          </div>
        </Card>
      </div>

      <ResultsPanel result={result} error={error} running={running} />
    </div>
  );
}
